import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
  Alert,
  Divider,
  Tooltip,
  IconButton,
  Chip,
  useMediaQuery,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import AccessControl, { FEATURE_IDS } from "../../../../components/AccessControl";
import { useNavigate } from "react-router-dom";
import { useMutation, useLazyQuery, useQuery } from "@apollo/client";
import {
  INFRA_VIEWS,
  GET_INTEGRATION_DETAILS,
  GET_INFRA_CHECKS,
  GENERATE_INFRA_CHECKS_EXCEL,
} from "../../../../apis/organization-apis/Infra-apis/InfraApis";
import { useSelector } from "react-redux";
import { getImage } from "../utils/utils";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import FilterBar from "../../../../components/FilterNSearchBar/FilterNSearchBar";
import { CheckStatusChip } from "../../../../components/CheckStatusChip/CheckStatusChip";
import { INFRA_STATUS, POLLING_CONFIG } from "../utils/enums";
import { CheckStatus, statusFilterOptions } from "../../../../utils/enums";
import { CheckCategory } from "../../../../utils/enums";
import useAppStore from "../../../../stores";
import DeactivatedIntegrationsAlert from "../../../../components/DeactivatedIntegrationsAlert/DeactivatedIntegrationsAlert";

// Service categories for different cloud providers
const awsServiceCategories = {
  Compute: ["EC2", "AWSLAMBDA"],
  Storage: ["S3", "EFS"],
  Database: ["DYNAMODB", "RDS"],
  Networking: ["VPC", "ROUTE53", "APIGATEWAY", "ELBV2"],
  Security: ["IAM", "ACM", "GUARDDUTY", "SECURITYHUB", "WAFV2"],
  Monitoring: ["CLOUDWATCH", "CLOUDTRAIL", "CONFIG"],
  "Content Delivery": ["CLOUDFRONT"],
  "AI/ML": ["BEDROCK"],
};

const azureServiceCategories = {
  Compute: ["VIRTUALMACHINES", "FUNCTIONS", "CONTAINERINSTANCES"],
  Storage: ["STORAGEACCOUNTS", "BLOBSERVICE", "FILESHARE"],
  Database: ["SQLDATABASE", "COSMOSDB", "REDISCACHE"],
  Networking: ["VIRTUALNETWORK", "LOADBALANCER", "APPLICATIONGATEWAY"],
  Security: ["KEYVAULT", "ACTIVEDIRECTORY", "SECURITYCENTER"],
  Monitoring: ["MONITOR", "APPLICATIONINSIGHTS", "LOGANALYTICS"],
  "Content Delivery": ["CDN", "FRONTDOOR"],
  "AI/ML": ["MACHINELEARNING", "COGNITIVESERVICES"],
};

const gcpServiceCategories = {
  Compute: ["COMPUTEENGINE", "CLOUDFUNCTIONS", "CLOUDRUN"],
  Storage: ["CLOUDSTORAGE", "CLOUDSQL", "FILESTORE"],
  Database: ["CLOUDSQL", "FIRESTORE", "BIGTABLE", "SPANNER"],
  Networking: ["VPC", "CLOUDLOADBALANCING", "CLOUDCDN"],
  Security: ["IAM", "CLOUDKMS", "SECURITYCOMMANDCENTER"],
  Monitoring: ["CLOUDMONITORING", "CLOUDLOGGING", "ERRORREPORTING"],
  "Content Delivery": ["CLOUDCDN"],
  "AI/ML": ["AIPLATFORM", "AUTOML"],
};

// Helper function to get service categories based on infra type
const getServiceCategories = (infraType) => {
  const type = infraType?.toLowerCase();
  if (type === "aws") return awsServiceCategories;
  if (type === "azure") return azureServiceCategories;
  if (type === "gcp") return gcpServiceCategories;
  return {};
};

// Helper function to categorize service based on entity_type
const categorizeService = (entityType, serviceCategories) => {
  if (!entityType || !serviceCategories) return "Other Services";

  const upperEntityType = entityType?.toUpperCase();

  for (const [category, services] of Object.entries(serviceCategories)) {
    if (services.includes(upperEntityType)) {
      return category;
    }
  }

  return "Other Services";
};

const InfrastructureOverview = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const infra = useSelector((state) => state.infra.infraType);
  const selectedFrameworkId = useAppStore((state) => state.selectedFrameworkId);

  // Define infraType before using it in hooks
  let infraType = infra ? infra : localStorage.getItem("infraType");

  const [generateInfraViews, { refetch: refetchGenerateInfraViews }] =
    useMutation(INFRA_VIEWS);
  const [getIntegrationDetails, { refetch: refetchIntegrationDetails }] =
    useLazyQuery(GET_INTEGRATION_DETAILS, {
      fetchPolicy: "network-only",
    });
  const [generateInfraChecksExcel, { loading: exportLoading }] =
    useMutation(GENERATE_INFRA_CHECKS_EXCEL);

  const [infraData, setInfraData] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableFilters, setTableFilters] = useState({});

  // Snackbar state for error handling
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Fetch checks data
  const {
    data: checksData,
    loading: loadingChecks,
    refetch: refetchChecks,
  } = useQuery(GET_INFRA_CHECKS, {
    variables: {
      page: 0,
      limit: 5000,
      filter: {
        framework_id: selectedFrameworkId,
        integrationKey: infraType?.toLowerCase(),
      },
    },
    skip: !infraType || !selectedFrameworkId,
  });

  useEffect(() => {
    let fetchInfraViewsData = async () => {
      try {
        setIsLoading(true);

        // Send both requests in parallel since they are independent
        const [integrationDetailsRes, infraViewsRes] = await Promise.all([
          getIntegrationDetails({
            variables: {
              integrationKey: infraType,
            },
          }),
          generateInfraViews({
            variables: {
              integrationKey: infraType,
              framework_id: selectedFrameworkId,
            },
          }),
        ]);

        const integrationDetailsData = integrationDetailsRes?.data;

        // Merge mutation response with query data
        const mergedData = {
          ...infraViewsRes?.data,
          ...integrationDetailsData,
        };
        setInfraData(mergedData);

        // Check if we should start polling based on current status
        const currentRunStatus =
          integrationDetailsData?.getIntegrationDetails?.data?.run_status;
        if (currentRunStatus === INFRA_STATUS.RUNNING && !shouldPoll) {
          setShouldPoll(true);
        }
      } catch (error) {
        console.error("error", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only call the function if we have infraType
    if (infraType) {
      fetchInfraViewsData();
    }
  }, [
    generateInfraViews,
    getIntegrationDetails,
    infraType,
    shouldPoll,
    selectedFrameworkId,
  ]);

  useEffect(() => {
    if (shouldPoll) {
      // Reset poll count when starting to poll
      setPollCount(0);

      const interval = setInterval(async () => {
        try {
          const res = await refetchIntegrationDetails();
          const newRunStatus = res.data.getIntegrationDetails?.data?.run_status;

          setInfraData((prev) => ({
            ...prev,
            getIntegrationDetails: res.data.getIntegrationDetails,
          }));

          setPollCount((prevCount) => {
            const newCount = prevCount + 1;

            // Check if we should stop polling
            if (
              newRunStatus === INFRA_STATUS.COMPLETED ||
              newRunStatus === INFRA_STATUS.FAILED ||
              newCount >= POLLING_CONFIG.MAX_ATTEMPTS
            ) {
              clearInterval(interval);
              setShouldPoll(false);
            }

            return newCount;
          });
        } catch (err) {
          console.error("Polling error:", err);
          setPollCount((prevCount) => {
            const newCount = prevCount + 1;
            if (newCount >= POLLING_CONFIG.MAX_ATTEMPTS) {
              clearInterval(interval);
              setShouldPoll(false);
            }
            return newCount;
          });
        }
      }, POLLING_CONFIG.INTERVAL);

      return () => clearInterval(interval);
    }
  }, [shouldPoll, refetchIntegrationDetails]);

  // Reset poll count when polling stops
  useEffect(() => {
    if (!shouldPoll) {
      setPollCount(0);
    }
  }, [shouldPoll]);

  // Handle page visibility changes and component focus
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // When page becomes visible again, check if we should still be polling
      if (!document.hidden && shouldPoll) {
        try {
          // Immediately refetch to get current status
          const res = await refetchIntegrationDetails();
          const currentRunStatus =
            res.data.getIntegrationDetails?.data?.run_status;

          // Update the data
          setInfraData((prev) => ({
            ...prev,
            getIntegrationDetails: res.data.getIntegrationDetails,
          }));

          // If integration is completed or failed, stop polling
          if (
            currentRunStatus === INFRA_STATUS.COMPLETED ||
            currentRunStatus === INFRA_STATUS.FAILED
          ) {
            setShouldPoll(false);
          }
        } catch (error) {
          console.error(
            "Error checking integration status on page visibility:",
            error
          );
        }
      }
    };

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Also check when component mounts if polling is active
    if (shouldPoll) {
      handleVisibilityChange();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [shouldPoll, refetchIntegrationDetails]);

  // Function to handle immediate data update when integration is submitted
  const handleIntegrationSubmit = async (updatedIntegrationData) => {
    try {
      // If we received updated data from the dialog, use it immediately
      if (updatedIntegrationData) {
        setInfraData((prev) => ({
          ...prev,
          getIntegrationDetails:
            updatedIntegrationData.data.getIntegrationDetails,
        }));
      } else {
        // Fallback: refetch the integration details to get the updated status
        const updatedData = await refetchIntegrationDetails();
        setInfraData((prev) => ({
          ...prev,
          getIntegrationDetails: updatedData.data.getIntegrationDetails,
        }));
      }

      // Start polling
      setShouldPoll(true);
    } catch (error) {
      console.error("Error updating integration data:", error);
      // Still start polling even if immediate update fails
      setShouldPoll(true);
    }
  };

  const handleRoute = () => {
    let selectedInfra = infraType.toUpperCase();
    navigate(`/infrastructure/overview/${selectedInfra}`);
  };

  // Function to handle integration errors
  const handleIntegrationError = (errorMessage) => {
    setSnackbarMessage(errorMessage);
    setSnackbarOpen(true);
  };

  const allChecks = useMemo(() => {
    return checksData?.getInfraChecks?.data || [];
  }, [checksData]);

  // Helper function to format entity type for display
  const renderModifiedEntity = (entityType) => {
    if (!entityType) return "";
    const formattedEntityType = entityType
      .split("_")
      .map((word) => {
        const upper = word?.toUpperCase();
        if (upper === "AWS") return "AWS";
        if (upper === "AZURE") return "Azure";
        if (upper === "GCP") return "GCP";
        return upper;
      })
      .join(" ");
    return formattedEntityType;
  };

  // Extract unique entity types from checks
  const uniqueEntityTypes = useMemo(() => {
    const entityTypes = new Set();
    allChecks.forEach((check) => {
      if (check.entity_type) {
        entityTypes.add(check.entity_type);
      }
    });
    return Array.from(entityTypes);
  }, [allChecks]);

  // Get unique entity types for filter options
  const entityTypeFilterOptions = useMemo(() => {
    return uniqueEntityTypes.map((entityType) => ({
      id: entityType,
      label: renderModifiedEntity(entityType),
    }));
  }, [uniqueEntityTypes]);

  // Responsive column widths for smaller screens
  const isBelow900 = useMediaQuery("(max-width:900px)");
  const isBelow1024 = useMediaQuery("(max-width:1024px)");
  const isBelow1200 = useMediaQuery("(max-width:1200px)");

  const checkColWidth = isBelow900
    ? "45%"
    : isBelow1024
      ? "50%"
      : isBelow1200
        ? "58%"
        : "65%";

  const serviceColWidth = isBelow900
    ? "22%"
    : isBelow1024
      ? "20%"
      : isBelow1200
        ? "18%"
        : "20%";

  const statusColWidth = isBelow900
    ? "22%"
    : isBelow1024
      ? "20%"
      : isBelow1200
        ? "15%"
        : "10%";

  const actionsColWidth = isBelow900
    ? "11%"
    : isBelow1024
      ? "10%"
      : isBelow1200
        ? "9%"
        : "5%";

  // Checks table columns
  const checksColumns = [
    {
      label: "Check",
      field: "check_info",
      width: checkColWidth,
      align: "left",
    },
    {
      label: "Service",
      field: "entity_type",
      width: serviceColWidth,
      align: "center",
      filterConfig: {
        filterOptions: entityTypeFilterOptions,
      },
    },
    {
      label: "Status",
      field: "status",
      width: statusColWidth,
      align: "left",
      filterConfig: {
        filterOptions: statusFilterOptions,
      },
    },
    {
      label: "",
      field: "actions",
      width: actionsColWidth,
      align: "center",
    },
  ];

  const renderCheckCell = (row, column) => {
    switch (column?.field) {
      case "check_info":
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color={theme.customColors.text.strong}
              sx={{ mb: 0.5, lineHeight: 1.4 }}
            >
              {row.active_check_name}
            </Typography>
            <Tooltip
              title={row.active_check_description || "-"}
              arrow
              placement="top"
            >
              <Typography
                variant="body2"
                color={theme.customColors.text.secondary}
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {row.active_check_description || "-"}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "entity_type":
        return (
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                textAlign: "center",
                width: "100%",
              }}
            >
              {renderModifiedEntity(row.entity_type)}
            </Typography>
          </Box>
        );
      case "status":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <CheckStatusChip status={row.status} />
          </Box>
        );
      case "actions":
        return (
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Tooltip title="View Details" arrow>
              <IconButton
                size="small"
                onClick={() => handleViewCheckDetails(row)}
                sx={{
                  color: theme.customColors.icons.dark,
                  "&:hover": {
                    backgroundColor: theme.customColors.background.light,
                    transform: "translateX(2px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <OpenInNewRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      default:
        return (
          <Typography variant="body2" color={theme.customColors.text.primary}>
            {row[column.field]}
          </Typography>
        );
    }
  };

  // Handle view check details navigation
  const handleViewCheckDetails = (row) => {
    const checkType = row.check_type;
    window.open(`/checks/${checkType}`, "_blank");
  };

  // Filter checks based on search and table filters
  const filteredChecks = useMemo(() => {
    return allChecks?.filter((check) => {
      const matchesSearch =
        !searchQuery ||
        (check?.active_check_name ?? "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (check?.active_check_description ?? "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (check?.active_check_id ?? "")
          .toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesEntityType =
        !tableFilters.entity_type ||
        tableFilters.entity_type.length === 0 ||
        tableFilters.entity_type.includes(check.entity_type);

      const matchesStatus =
        !tableFilters.status ||
        tableFilters.status.length === 0 ||
        tableFilters.status.includes(check.status);

      return matchesSearch && matchesEntityType && matchesStatus;
    });
  }, [allChecks, searchQuery, tableFilters]);

  const clearFilters = () => {
    setSearchQuery("");
    setTableFilters({});
  };

  const handleFilterChange = (filters) => {
    setTableFilters(filters);
  };

  const activeCheckCategoryByInfra = {
    aws: CheckCategory.AWS_SYSTEM_CHECK,
    azure: CheckCategory.AZURE_SYSTEM_CHECK,
    gcp: CheckCategory.GCP_SYSTEM_CHECK,
  };
  const activeCheckCategory =
    activeCheckCategoryByInfra[infraType?.toLowerCase()];

  const handleExportToExcel = async () => {
    if (!activeCheckCategory) {
      setSnackbarMessage("Export is not available for this infrastructure type.");
      setSnackbarOpen(true);
      return;
    }
    try {
      const { data } = await generateInfraChecksExcel({
        variables: {
          framework_id: selectedFrameworkId ?? 0,
          active_check_category: [activeCheckCategory],
          status:
            tableFilters.status?.length > 0 ? tableFilters.status : undefined,
          entity_type:
            tableFilters.entity_type?.length > 0
              ? tableFilters.entity_type
              : undefined,
        },
      });
      const url = data?.generateInfraChecksExcel?.url;
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = "";
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setSnackbarMessage("Export failed: no download URL returned.");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage(err?.message || "Export failed.");
      setSnackbarOpen(true);
    }
  };

  const hasActiveFilters =
    (tableFilters.status && tableFilters.status.length > 0) ||
    (tableFilters.entity_type && tableFilters.entity_type.length > 0);
  const exportButtonLabel = hasActiveFilters ? "Export filtered" : "Export";

  return (
    <Box
      sx={{
        width: "100%",
        py: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <DeactivatedIntegrationsAlert
        checkCategory={activeCheckCategory || ""}
        onManageIntegrations={() =>
          navigate("/integrations?domain=cloud_provider")
        }
      />
      {/* Main Checks Card */}
      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.customColors.border.strong}`,
          boxShadow: theme.customColors.shadow.main,
          backgroundColor: theme.palette.background.paper,
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                Checks
              </Typography>
              {(searchQuery ||
                (tableFilters.entity_type &&
                  tableFilters.entity_type.length > 0) ||
                (tableFilters.status && tableFilters.status.length > 0)) && (
                  <Chip
                    label="Filtered"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: theme.customColors.text.secondary,
                      borderColor: theme.customColors.border.main,
                      background: theme.customColors.background.light,
                    }}
                  />
                )}
            </Box>
          }
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.customColors.background.cardHeader,
          }}
          action={
            <AccessControl featureId={FEATURE_IDS.INFRA_OVERVIEW_EXPORT_CHECKS}>
              <Tooltip
                title={
                  hasActiveFilters
                    ? "Export filtered checks to Excel"
                    : "Export checks data to Excel"
                }
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={
                      exportLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <DownloadIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    onClick={handleExportToExcel}
                    disabled={!filteredChecks?.length || exportLoading}
                    sx={{
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      borderColor: theme.customColors.border.main,
                      color: theme.customColors.text.strong,
                      "&:hover": {
                        borderColor: theme.customColors.border.strong,
                        backgroundColor: theme.customColors.background.hover,
                      },
                      "&:disabled": {
                        borderColor: theme.customColors.border.disabled,
                        color: theme.customColors.text.disabled,
                      },
                    }}
                  >
                    {exportButtonLabel}
                  </Button>
                </span>
              </Tooltip>
            </AccessControl>
          }
        />

        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              backgroundColor: theme.customColors.background.main,
            }}
          >
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={(e) => setSearchQuery(e.target.value)}
              filterLabel=""
              filters={[]}
              selectedFilter={null}
              onFilterChange={() => { }}
              onClearFilters={clearFilters}
              countLabel={`${filteredChecks.length} check${filteredChecks.length !== 1 ? "s" : ""
                } found`}
            />
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          <Box>
            {allChecks.length === 0 && !loadingChecks ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 12,
                  px: 3,
                }}
              >
                <Typography
                  variant="h5"
                  color="textSecondary"
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  No checks found
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 4, maxWidth: 400, mx: "auto", lineHeight: 1.5 }}
                >
                  No checks are currently available for this infrastructure.
                  Make sure the infrastructure is properly configured and
                  synced.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  "& table": {
                    minWidth: "unset !important",
                  },
                }}
              >
                <ReusableTable
                  columns={checksColumns}
                  data={filteredChecks}
                  renderCell={renderCheckCell}
                  height="auto"
                  showPagination={true}
                  loading={loadingChecks || isLoading}
                  rowsPerPageOptions={[50, 100]}
                  onFilterChange={handleFilterChange}
                  filters={tableFilters}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InfrastructureOverview;
